import { useCallback, useEffect, useRef } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { useInView } from 'react-intersection-observer';
import { tc } from '../lib/trackingContext';

export const useModuleTracking = ({
  name,
  attachClickHandler = true
}: {
  name: string | null;
  attachClickHandler?: boolean;
}) => {
  const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true });

  const containerRef = useRef<HTMLDivElement>(null);

  const sibling = containerRef.current?.previousElementSibling;
  const prevDividerModuleName =
    sibling && sibling.classList.contains('DividerModule') ? sibling.textContent : '';

  const onClick = useCallback(
    (event: Event | React.MouseEvent) => {
      const clickedAnchor = (event.target as HTMLElement).closest('a');
      if (containerRef.current && containerRef.current.contains(clickedAnchor)) {
        tc.track('Module Clicked', {
          addToScopeOnce: {
            moduleName: name || prevDividerModuleName
          },
          addToTrackingEvent: { label: name, category: 'Modules', nonInteraction: 1 }
        });
      }
    },
    [prevDividerModuleName, name]
  );

  useEffect(() => {
    const current = containerRef.current;
    if (attachClickHandler) {
      current?.addEventListener('mousedown', onClick);
      return () => {
        current?.removeEventListener('mousedown', onClick);
      };
    }
  }, [onClick, attachClickHandler]);

  useEffect(() => {
    if (inView) {
      tc.track('Module Viewed', {
        addToScopeOnce: { moduleName: name || prevDividerModuleName },
        addToTrackingEvent: { nonInteraction: 1, label: name, category: 'Modules' }
      });
    }
  }, [inView, name, prevDividerModuleName]);

  return { ref: composeRefs<HTMLDivElement>(ref, containerRef), onClick };
};
